import { Component, EventEmitter, Inject, OnInit, Output, TemplateRef } from '@angular/core';
import { Task, DeviceInfo, Scoutview, SensorGroup, UserAccessRole, UserGroup, Event, SensorReadingType, WaterSchedule, EventType, UserAccessType, EventAction, ActionType, WaterScheduleInfo, Device, PtzControlResults, DeviceCamera, PTZCameraPreset, CameraPreview, MediaCaptureResults, CameraType, ImageCaptureMethod, ScoutviewImage, TaskPriority, TaskType, ImageDevice } from '@models';
import { TimerRound } from '@models/Irrigation/timerRound';
import { DeviceService } from '@services/device.service';
import { HubConnectionFactory } from '@services/hub-connection-factory.service';
import { SensorGroupService } from '@services/sensorgroup.service';
import { UserService } from '@services/user.service';
import { AppComponent } from 'app/app.component';
import { EditEventsAndActionsModalComponent } from 'app/components/trigger-settings/edit-events-and-actions-modal/edit-events-and-actions-modal.component';
import { EventMapper } from 'app/components/trigger-settings/trigger.mapping';
import { TriggerService } from 'app/components/trigger-settings/trigger.service';
import { Authorization } from 'app/shared/classes/authorization';
import { CommonShared } from 'app/shared/classes/common';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { KigSettings, KIG_SETTINGS } from 'settings/kig-settings';
import {HubConnectionState} from '@microsoft/signalr';
import {EMPTY, from, Observable, Subject} from 'rxjs';
import { DeviceMapping } from 'app/shared/classes/deviceMapping';
import { ScoutviewService } from '@services/scoutview.service';
import { FailedToStartTransportError } from '@microsoft/signalr/dist/esm/Errors';
import { ChangeLogNotesParams, ChangeLogNotesComponent } from 'app/components/messages/changelog-notes/changelognotes.components';
import { json } from 'd3';
import { MediaGallery } from '../scoutview.component';
import { DataShareService } from '@services/dataShare.service';


export interface CameraSettingsParams {
  selectedDevice: Device;
  devices: Device[];
  imageDevices: ImageDevice[];
  scoutviews: Scoutview[];
  associatedUserGroups: UserGroup[];
  userGroups: UserGroup[];
  sensorReadingTypes: SensorReadingType[];
  refreshImages: any;
}

export interface EditEventsAndActionsModalParams {
  events: Event[];
  event: Event;
  changeLogNotes: string;
  username: string;
  selectedUserGroup: UserGroup;
  preEditObjectAsJSONString: string;
  eventTitle: string;
  associatedUserGroups: UserGroup[];
  evaluateTimes: string[];
  sensorDataTypes: SensorReadingType[];
  sensorGroups: SensorGroup[];
  TimingSingleDDOptions: string[];
  IrrDestinationDDOptions: string[];
  CamGatewayDDOptions: string[];
  IrrZoneDDOptions: string[];
  globalWaterSchedules: WaterSchedule[];
  buttonValue: string;
  devices: DeviceInfo[];
}

const zeroPad = (num: number, places: number) => String(num).padStart(places, '0');

@Component({
  standalone: false,
  selector: 'app-camera-settings',
  templateUrl: './camera-settings.component.html',
  styleUrls: ['./camera-settings.component.scss']
})

export class CameraSettingsComponent implements OnInit, CameraSettingsParams {

  private eventMapper = new EventMapper();
  public auth = new Authorization();
  public common = new CommonShared();
  public deviceMapper = new DeviceMapping();

  modalDragOn: boolean = false;

  scoutviews: Scoutview[];
  events: Event[];
  event: Event = new Event();
  refreshImages: any;

  userGroups: UserGroup[] = [];
  associatedUserGroups: UserGroup[] = [];
  associatedCameraEvents: Event[] = [];
  sensorReadingTypes: SensorReadingType[] = [];

  public cameraEventsCols = [
    {field: 'name', header: 'Name', width: '22%'},
    {field: 'action', header: 'Action(s)', width: '22%'},
    {field: 'eventType', header: 'Type', width: '14%'},
    {field: 'modifiedDate', header: 'Modified', width: '22%'},
    {field: 'isDisabled', header: 'Enabled', width: '10%'},
    {field: 'edit', header: '', width: '5%'},
    {field: 'delete', header: '', width: '5%'}
  ];

  devices: Device[] = [];
  associatedDevices: Device[] = [];
  selectedDevice: Device;
  selectedDeviceOwner: string = "";
  imageDevices: ImageDevice[];
  selectedCamera: DeviceCamera;
  selectedCameraPreset: PTZCameraPreset;

  cameraPreview: CameraPreview[];

  public deviceConfig = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select Camera/Gateway',
    noResultsFound: 'No cameras found!',
    searchOnKey: 'name',
    limitTo: 0
  }; 

  public cameraConfig = {
    displayKey: 'name',
    search: false,
    height: '300px',
    placeholder: 'Select Camera',
    noResultsFound: 'No cameras found! - Add cameras on Gateway page.',
    searchOnKey: 'name',
    limitTo: 0
  };

  captureImageNowResolution: string = "high";
  captureVideoNowResolution: string = "high";
  captureVideoNowDuration: number = 5;

  logModalRef: BsModalRef;
  username = 'unknown';
  preEditObjectAsJSONString = '';
  changeLogNotes = '';

  eventsLoaded = false;
  associatedUserGroupsLoaded = false;
  userGroupsLoaded = false;
  sensorGroupsLoaded = false;
  selectedUserGroup: UserGroup;
  readonly globalSwitch: boolean;
  
  evaluateTimes: string[] = [];
  IrrDestinationDDOptions: string[];
  CamGatewayDDOptions: string[];
  IrrZoneDDOptions: string[];
  TimingSingleDDOptions: string[] = [];
  sensorDataTypes: SensorReadingType[] = [];
  sensorGroups: SensorGroup[] = [];
  globalWaterSchedules: WaterSchedule[] = [];

  ptzCameraControlRef: BsModalRef;
  waitingForPTZResponse: boolean = false;
  ptzCameraFocus: number = 0;
  ptzCameraZoom: number = 0;
  ptzCameraStep: number = 1;
  ptzImagePreview: string;
  ptzImageblobName: string;
  ptzCameraAutoFocus: boolean = false;
  enableRemoteAccessToCameraDuration: string = "5";

  private hubConnection: signalR.HubConnection;
  previewImageTagsLinked: boolean = true;

  // Fullscreen viewer state
  public showFullscreenViewer: boolean = false;
  public viewerImageSrc: string = '';
  public viewerImageLoading: boolean = false;
  public viewerZoomLevel: number = 1;
  public viewerPanX: number = 0;
  public viewerPanY: number = 0;
  private viewerIsPanning: boolean = false;
  private viewerLastX: number = 0;
  private viewerLastY: number = 0;
  public viewerBoxZoomMode: boolean = false;
  public boxZoomActive: boolean = false;
  private boxStartX: number = 0;
  private boxStartY: number = 0;
  public boxCurrentX: number = 0;
  public boxCurrentY: number = 0;


  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService, private hubFactory: HubConnectionFactory,
    private deviceService: DeviceService, public global: AppComponent, private confirmationService: ConfirmationService, private _scoutviewService: ScoutviewService,
    @Inject(KIG_SETTINGS) private settings: KigSettings, public _userService: UserService, private _triggerService: TriggerService,
     private _sensorGroupService: SensorGroupService, private dataShareService: DataShareService) {
      
    this.globalSwitch = global.globalSwitch;

    this.hubConnection = this.hubFactory.create('/api/hubs/eventaction');
    this.hubConnection.onclose(() => console.log('hubConnection.onclose') );
    this.hubConnection.onreconnecting(() => console.log('Attempting reconnect...') );
    this.hubConnection.onreconnected(() => console.log('Reconnected.') );
    this.hubConnection.on('refreshEvent', (updateType: string, updatedEvent: string) => this.refreshEvent(updateType, updatedEvent));
  }


  ngOnInit(): void {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      () => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp(): void {
    this.buildGatewayDDList();
    this.getEvents();
    this.startSignalR();
    this.loadEvaluationTimes();
    this.newCameraPresetSelection();
    this.initializeCameraPreviews();
  }
  

  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  private startSignalR(): Observable<any> {
    return (this.hubConnection.state === HubConnectionState.Disconnected) ? from(this.hubConnection.start()) : EMPTY;
  }

  public ngOnDestroy(): void {
    this.stopSignalR();
  }

  private stopSignalR() {
    this.hubConnection.stop();
  }

  private refreshEvent(updateType: string, updatedEvent: string) {
    switch (updateType.toLowerCase()) {
    case 'edit':
      this.refreshEventOnEdit(updatedEvent);
      break;
    case 'new':
      this.refreshEventOnNew(updatedEvent);
      break;
    case 'del':
      this.refreshEventOnDel(updatedEvent);
      break;
    default:
      break;
    }
  }


  public refreshEventOnEdit(updatedEvent: string): void {
    this._triggerService.clearCachedVariables();

    this.eventMapper.devices = this.deviceMapper.mapDevices2DeviceInfos(this.devices);
    const eventInfo = this.eventMapper.parseUCaseEventInfoObj2LCaseEventInfo(updatedEvent);
    const event: Event = this.eventMapper.mapEventInfo2Event(eventInfo);
    this.auth.mapUserAccessForEvent(event, this.globalSwitch, this.username, this.userGroups);
    this.updateEventActionSchedules(event);

    event.sensorConditions.forEach(s => {
      s.sensorGroup = this.sensorGroups.find(x => x.id == s.sensorGroupID);
      s.sensorReadingType.type = this.getNameForSensorReadingTypeID(s.sensorGroup.id, s.sensorReadingType.id);
    });

    const matchedEventIndex = this.events.findIndex(e => e.id === event.id);
    if (matchedEventIndex < 0) {
      return;
    }

    this.events[matchedEventIndex] = event;
    this.events = [...this.events];
    this.updateSelectedDevice(this.selectedDevice);
  }


  public refreshEventOnNew(updatedEvent: string): void {
    this._triggerService.clearCachedVariables();

    this.eventMapper.devices = this.deviceMapper.mapDevices2DeviceInfos(this.devices);
    const eventInfo = this.eventMapper.parseUCaseEventInfoObj2LCaseEventInfo(updatedEvent);
    const event: Event = this.eventMapper.mapEventInfo2Event(eventInfo);
    this.auth.mapUserAccessForEvent(event, this.globalSwitch, this.username, this.userGroups);
    this.updateEventActionSchedules(event);

    event.sensorConditions.forEach(s => {
      s.sensorGroup = this.sensorGroups.find(x => x.id == s.sensorGroupID);
      s.sensorReadingType.type = this.getNameForSensorReadingTypeID(s.sensorGroup.id, s.sensorReadingType.id);
    });

    this.events = [...this.events, event];
    this.updateSelectedDevice(this.selectedDevice);
  }


  public refreshEventOnDel(updatedEventID: string): void {
    this._triggerService.clearCachedVariables();
    
    const id: number = parseInt(updatedEventID, 10);
    const eventIndex = this.events.findIndex(e => e.id === id);
    if (eventIndex > -1) {
      const eventName = this.events[eventIndex].name;
      this.events.splice(eventIndex, 1);
      this.events = [...this.events];
      this.updateSelectedDevice(this.selectedDevice);
    }
  }


  loadEvaluationTimes(): void {
    this.evaluateTimes = [];
    for (let hour = 0; hour < 24; hour++) {
      this.evaluateTimes.push ( zeroPad(hour,2) + ':15' );
      this.evaluateTimes.push ( zeroPad(hour,2) + ':45' );
    }
  }


  buildGatewayDDList() {
    this.IrrDestinationDDOptions = [];
    this.CamGatewayDDOptions = [];

    this.devices.forEach(d => {
      if (d.userName === this.username || this.associatedUserGroups.findIndex(u => u.id === d.userGroupId) > -1) {
        if (d.serialNumber !== '') {
          this.IrrDestinationDDOptions.push(d.name);
          this.CamGatewayDDOptions.push(d.name);
        }
      }
    });
  }


  initializeCameraPreviews() {
    this.cameraPreview = [];

    if (this.selectedDevice.deviceCameras != undefined) {
      this.selectedDevice.deviceCameras.forEach(x => {
        var cameraPreview: CameraPreview = {
          imageTags: "",
          deviceCamera: x,
          scoutview: {
            image: {
              isLoading: false,
              blobName: '',
              title: '',
              source: '',
              needsRefresh: false
            },
            latestThumbnail: {
              isLoading: false,
              blobName: '',
              title: '',
              source: '',
              needsRefresh: false
            },
            origThumbnail: {
              isLoading: false,
              blobName: '',
              title: '',
              source: '',
              needsRefresh: false
            },
            svMetadata: {
              id: 0,
              captureDate: undefined,
              expireDate: undefined,
              modifiedDate: undefined,
              sourceId: '',
              sourceName: '',
              tags: [],
              //cameraType: CameraType.LinePower,
              //cameraModel: '',
              cameraId: 0,
              thumbnailType: 'jpg',
              fileName: '',
              fileSize: 0,
              imageDimensions: '',
              comments: '',
              captureMethod: ImageCaptureMethod.Manual,
              gpsLocation: undefined,
              svPipelineSequence: [],
              svPipelineCompletedDate: undefined,
              svPipelineResults: [],
              //userName: '',
              //userGroupId: 0,
              pipelineInfo: ''
            },
            selected: false,
            expireDays: 0,
            expandedMetadata: false,
            userName: '',
            userGroup: '',
            userGroupId: 0,
            latestThumbnailIsForMP4: false,
            showOriginal: false
          }
        }; 
        this.cameraPreview.push(cameraPreview);
      });
    }
  }


  parseMediaCaptureResults(jsonString: string): MediaCaptureResults {
    var mediaCaptureResults: MediaCaptureResults = {
      status: '',
      filename: '',
      focus: 0,
      zoom: 0
    };

    try {    
      mediaCaptureResults = JSON.parse(jsonString);
    }
    catch {
      if (jsonString.toLowerCase() == "success") {
        mediaCaptureResults.status = "success";
      }
    }

    return mediaCaptureResults;
  }


  clearPreview(previewId: number) {
    this.cameraPreview[previewId].scoutview.latestThumbnail.blobName = "";
    this.cameraPreview[previewId].scoutview.latestThumbnail.source = "";
    this.cameraPreview[previewId].scoutview.image.blobName = "";
    this.cameraPreview[previewId].scoutview.image.source = "";
  }


  captureImageNow(previewId: number, refreshOnly: boolean) {
    this.clearPreview(previewId);

    var device = this.devices.find(x => x.id == this.cameraPreview[previewId].deviceCamera.deviceId);

    let charactersToRemove = ",!@#$%^& "; 
    let regex = new RegExp(`[${charactersToRemove}]`, 'g');
    var tags = this.cameraPreview[previewId].imageTags.replace(regex, '');

    var useFilename = "";
    var cameraModel = this.cameraPreview[previewId].deviceCamera.model;

    if (refreshOnly) {
      useFilename = `${device.serialNumber}_20200101T010101_preview${previewId}.jpg`;
      this.deleteCameraImage(device.serialNumber, useFilename);
    }

    this.cameraPreview[previewId].scoutview.latestThumbnail.isLoading = true;

    var task: Task = { taskID: 0, deviceID: 0, clientID: '', priority: TaskPriority.high, type: TaskType.all,
                        action: '', payload: '', expireTimeSec: 0, taskWaitIntervalSec: 0, waitForResult: false };

    if (!cameraModel.includes('Reolink')) {
      task = this.buildNewTask(this.selectedDevice.id, `/home/pi/kigs/scripts/tasks/captureImage.sh useFilename=${useFilename} tags=${tags}`, '');
    }
    else {
      var user: string = '';
      var pass: string = '';
      var ip: string = '';
      var camera = this.cameraPreview[previewId].deviceCamera;
      var command = (refreshOnly) ? 'refresh' : 'saveimage';

      if (camera.login != undefined && camera.login != '' && camera.login.includes(':')) {
        user = camera.login.split(':')[0];
        pass = camera.login.split(':')[1];
        ip = camera.port;
        user = user.replace(/ /g, '');
        pass = pass.replace(/ /g, '');
        ip = ip.replace(/ /g, '');
      }
      task = this.buildNewTask(this.selectedDevice.id, `/home/pi/kigs/scripts/tasks/ptzControl user=${user} pass=${pass} filename=${useFilename} ipaddress=${ip} ${command.toLowerCase()}=${'1'} tags=${tags} cam_type=${this.cameraPreview[previewId].deviceCamera.power} gport=18`, '');
    }

    this.toastr.success('Request sent.  Wait a few seconds for image.');

    this.deviceService.postTask(task, '', 3).subscribe(
      results => {
        if (results != null) {
          var mediaCaptureResults = this.parseMediaCaptureResults(results);

          if (mediaCaptureResults.status == "success" && mediaCaptureResults.filename.includes('.')) {
            var thumbnail = mediaCaptureResults.filename.split('.')[0] + "_latestThumbnail.jpg";
            this.cameraPreview[previewId].scoutview.latestThumbnail.blobName = thumbnail;
            this.cameraPreview[previewId].scoutview.image.blobName = mediaCaptureResults.filename;
            this.cameraPreview[previewId].scoutview.svMetadata.sourceId = device.serialNumber;

            this.downloadImageAsBase64(device.serialNumber, thumbnail, false).subscribe(x => {
              this.cameraPreview[previewId].scoutview.latestThumbnail.source = x;
              this.cameraPreview[previewId].scoutview.latestThumbnail.isLoading = false;

              if (!refreshOnly) {
                this.refreshImages();
                this.cameraPreview[previewId].imageTags = "";
              }
            });
          }
          else {
            this.cameraPreview[previewId].scoutview.latestThumbnail.isLoading = false;
            this.toastr.warning('Error taking snapshot');
          }
        } else {
          this.toastr.warning('Error taking snapshot');
        }
      },
      err => {
        this.toastr.error('Error occurred while taking snapshot');
      }
    );
  }


  captureVideoNow(previewId: number) {
    if (this.cameraPreview[previewId].deviceCamera.model.includes('ArduCam')) {
      this.captureArduCamVideo(previewId);
    }
    else {
      this.ptzControl("capturevideo", this.captureVideoNowDuration, this.cameraPreview[previewId].deviceCamera, previewId);
    }
  }


  captureArduCamVideo(previewId: number) {
    this.clearPreview(previewId);

    var tags = this.cameraPreview[previewId].imageTags.replace(/\s/g, '');
    var device = this.devices.find(x => x.id == this.cameraPreview[previewId].deviceCamera.deviceId);

    this.cameraPreview[previewId].scoutview.latestThumbnail.isLoading = true;

    if (this.captureVideoNowDuration < 1) this.captureVideoNowDuration = 5;
    if (this.captureVideoNowDuration > 60) this.captureVideoNowDuration = 60;

    const task = this.buildNewTask(this.selectedDevice.id, `/home/pi/kigs/scripts/tasks/captureVideo.sh duration=${this.captureVideoNowDuration} tags=${tags}`, '');
    this.toastr.success('Request sent.  Wait a few seconds for video clip.');

    this.deviceService.postTask(task, '', 4).subscribe(
      results => {
        if (results != null) {
          var mediaCaptureResults = this.parseMediaCaptureResults(results);

          if (mediaCaptureResults.status == "success") {
            var image = mediaCaptureResults.filename;
            this.cameraPreview[previewId].scoutview.image.blobName = image;
            this.cameraPreview[previewId].scoutview.svMetadata.sourceId = device.serialNumber;

            this.downloadImageAsBase64(device.serialNumber, image, false).subscribe(x => {
              this.cameraPreview[previewId].scoutview.image.source = x;
              this.cameraPreview[previewId].scoutview.latestThumbnail.isLoading = false;

              this.refreshImages();
            });
          }
        } else {
          this.toastr.warning('Error taking video');
        }
      },
      err => {
        this.toastr.error('Error occurred while taking video');
      }
    );
  }


  toggleLinkingOfPreviewImageTags() {
    this.previewImageTagsLinked = !this.previewImageTagsLinked;
  }


  snapShotAllPreviewsAndClearTagsOnSuccess() {
    for (var i=0; i < this.cameraPreview.length; i++) {
      this.captureImageNow(i, false);
    }
  }


  propagateImageTagChanges(previewId: number) {
    if (this.previewImageTagsLinked) {
      this.cameraPreview.forEach(x => x.imageTags = this.cameraPreview[previewId].imageTags);
    }
  }


  getEvents(): void {
    this.eventsLoaded = false;
    this._triggerService.getEvents('Loading Events ...').subscribe(
      results => {
        if (results != null) {
          this.eventMapper.devices = this.deviceMapper.mapDevices2DeviceInfos(this.devices);
          this.events = this.eventMapper.mapEventInfos2Events(results);
          this.common.sortArrayByNameParm(this.events);

          this.events.forEach(e => {
            this.auth.mapUserAccessForEvent(e, this.globalSwitch, this.username, this.userGroups);
          });

          this.eventsLoaded = true;

          this.getIrrigationSchedules();

          if (this.sensorGroups.length < 1) {
            this.getSensorGroups();
          }

          this.updateSelectedDevice(this.selectedDevice);
        }
        else {
          this.toastr.warning('No Events found.');
        }
        return;
      },
      () => {
        this.toastr.error('Error occurred while retrieving Event list.');
      });
  }


  addEvent(): void {
    this.event = this.newEvent();
    this.setEventType(this.event.eventType.toString());
    this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === -1);
    this.event.userName = this.username;
    this.auth.mapUserAccessForEvent(this.event, this.globalSwitch, this.username, this.userGroups);
    const params: EditEventsAndActionsModalParams = {
      event: this.event,
      eventTitle: 'New Trigger',
      events: this.events,
      changeLogNotes: '',
      username: this.username,
      selectedUserGroup: this.selectedUserGroup,
      preEditObjectAsJSONString: this.preEditObjectAsJSONString,
      associatedUserGroups: this.associatedUserGroups,
      evaluateTimes: this.evaluateTimes,
      sensorDataTypes: this.sensorDataTypes,
      sensorGroups: this.sensorGroups,
      TimingSingleDDOptions: this.TimingSingleDDOptions,
      IrrDestinationDDOptions: this.IrrDestinationDDOptions,
      CamGatewayDDOptions: this.CamGatewayDDOptions,
      IrrZoneDDOptions: this.IrrZoneDDOptions,
      globalWaterSchedules: this.globalWaterSchedules,
      buttonValue: 'Create',
      devices: this.deviceMapper.mapDevices2DeviceInfos(this.devices)
    };
    this.modalService.show(EditEventsAndActionsModalComponent, { backdrop: false, ignoreBackdropClick: true, initialState: params });
  }

  
  newEvent(): Event {
    this.event.id = 0;
    return {
      id: this.event.id,
      name: '',
      description: '',
      userName: this.username,
      userGroup: '',
      userGroupID: null,
      userAccess: UserAccessType.ReadWrite,
      evaluateDaysOfWeek: [true, true, true, true, true, true, true],
      evaluateStartTime: '00:15',
      evaluateEndTime: '23:45',
      eventType: EventType.SensorBased,
      sensorConditions: [],
      timerConditions: [],
      modelConditions: [],
      motionConditions: [],
      eventActions: [this.newEventAction()],
      isDisabled: false,
      isDisabledColor: 'clear',
      createdDate: moment().local().toDate(),
      modifiedDate: moment().local().toDate(),
      ignoreUntilDate: moment().local().add(1, 'days').toDate(),
      checked: false,
      overallActionStatus: { state: 0, duration: 0 },
      useActionQueue: false,
      actionQueueId: 0
    };
  }


  setEventType(eventType: string): void {
    if (eventType === 'SensorBased') {
      this.event.eventType = EventType.SensorBased;
    } else if (eventType === 'TimerBased') {
      this.event.eventType = EventType.TimerBased;
    } else if (eventType === 'ModelBased') {
      this.event.eventType = EventType.ModelBased;
    }
  }

  
  newEventAction(): EventAction {
    return {
      id: 0,
      eventID: this.event.id,
      type: ActionType.Email,
      destination: '',
      deviceName: '',
      deviceSerial: '',
      zone: 20,
      scheduleName: '',
      scheduleID: -1,
      taskAction: '',
      onError: '',
      mediaResolution: 'Low',
      mediaDuration: 5,
      mediaDelay: 0,
      cameraId: 0,
      cameraName: '',
      cameraModel: '',
      cameraPresets: '',
      mediaActionOptions: [],
      sourceZone: 21,
      fillZone: 22,
      fillScheduleName: '',
      fillScheduleId: -1,
      drainZone: 23,
      drainScheduleName: '',
      drainScheduleId: -1,
      irrigationActionOptions: undefined,
      notificationSectionOptions: undefined
    };
  }


  editEvent(event: Event): void {
    this.changeLogNotes = '';

    if (event.userGroupID == null) {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === -1);
    } else {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id === event.userGroupID);
    }

    this.updateDeviceAndCameraNamesInEventActions(event);

    this.event = this.cloneEvent(event);
    this.preEditObjectAsJSONString = JSON.stringify(this.event);

    const params: EditEventsAndActionsModalParams = {
      event: this.event,
      eventTitle: event.name,
      events: this.events,
      changeLogNotes: this.changeLogNotes,
      username: this.username,
      selectedUserGroup: this.selectedUserGroup,
      preEditObjectAsJSONString: this.preEditObjectAsJSONString,
      associatedUserGroups: this.associatedUserGroups,
      evaluateTimes: this.evaluateTimes,
      sensorDataTypes: this.sensorDataTypes,
      sensorGroups: this.sensorGroups,
      TimingSingleDDOptions: this.TimingSingleDDOptions,
      IrrDestinationDDOptions: this.IrrDestinationDDOptions,
      CamGatewayDDOptions: this.CamGatewayDDOptions,
      IrrZoneDDOptions: this.IrrZoneDDOptions,
      globalWaterSchedules: this.globalWaterSchedules,
      buttonValue: 'Update',
      devices:  this.deviceMapper.mapDevices2DeviceInfos(this.devices)
    };
    this.modalService.show(EditEventsAndActionsModalComponent, { backdrop: false, ignoreBackdropClick: true, initialState: params});
  }


  onDeleteConfirm(event: Event): void {
    this.confirmationService.confirm({
      message: 'Delete the Event named \'' + event.name + '\'?',
      header: 'Delete Confirmation',
      icon: 'glyphicon glyphicon-warning-sign',
      accept: () => {
        this._triggerService.deleteEvent(this.eventMapper.mapEvent2EventInfo(event), this.username).subscribe(
          results => {
            if (results != null) {
              //this.createMessageData(MessageActionType.Deleted, event, '');
              this.toastr.success('Success');
            } else {
              this.toastr.warning('No trigger found.');
            }
            return;
          },
          () => {
            this.toastr.error('Error occurred while deleting trigger. Reload page');
          });
      },
      reject: () => {
      }
    });
  }


  cloneEvent(event: Event): Event {
    const newEvent: Event = JSON.parse(JSON.stringify(event));
    newEvent.createdDate = event.createdDate;
    newEvent.modifiedDate = event.modifiedDate;
    return newEvent;
  }


  updateDeviceAndCameraNamesInEventActions(event: Event): void {
    if (event.eventActions == null) {
      return;
    }
    event.eventActions.forEach(a => {
      a.deviceName = this.getDeviceNameFromSerial(a.deviceSerial);
      a.cameraName = this.getCameraNameFromSerialAndCameraId(a.deviceSerial, a.cameraId);
    });
  }


  getDeviceNameFromSerial(serial: string): string {
    const device = this.devices.find(d => d.serialNumber === serial);

    if (device === null || device === undefined || device.name === '') {
      return 'unknown';
    }
    return device.name;
  }


  getCameraNameFromSerialAndCameraId(serial: string, cameraId: number): string {
    const device = this.devices.find(d => d.serialNumber === serial);

    if (device != undefined) {
      var camera = device.deviceCameras.find(x => x.id == cameraId);
      return (camera != undefined) ? camera.name : "";
    }
    return "";
  }


  getIrrigationSchedules(): void {
    this.deviceService.getGlobalWaterSchedules().subscribe(
      results => {
        if(results && results.length !== 0){
          this.globalWaterSchedules = this.convertWaterScheduleInfos2WaterSchedules(results);
          this.common.sortArrayByNameParm(this.globalWaterSchedules);
          this.events.forEach(e => this.updateEventActionSchedules(e) );
        }
      },
      () => {
        this.toastr.error('Error occurred while retrieving irrigation schedules from the server');
      });
  }

  
  convertWaterScheduleInfos2WaterSchedules(waterScheduleInfos: WaterScheduleInfo[]): WaterSchedule[] {
    const waterSchedules: WaterSchedule[] = [];

    if (waterScheduleInfos == null) {
      return waterSchedules;
    }

    waterScheduleInfos.forEach( wsInfo => {
      let timerRounds: TimerRound[] = [];

      if (wsInfo.timerRounds != null) {
        timerRounds = JSON.parse(wsInfo.timerRounds);
      }

      const waterSchedule: WaterSchedule = {
        id: wsInfo.id,
        name: wsInfo.name,
        isEnabled: wsInfo.isEnabled,
        isSelfOwned: wsInfo.isSelfOwned,
        cycles: 0,
        timeOff: 0,
        timeOn: 0,
        timerRounds,
        usedByCount: 0,
        usedByTrigger: [],
        ownerID: -1,
        ownerName: '',
        ownerChanged: false
      };

      waterSchedules.push(waterSchedule);
    });

    return waterSchedules;
  }


  updateEventActionSchedules(event: Event): void {
    event.eventActions.forEach(a => {
      if (a.type === ActionType.Irrigation) {
        if (a.scheduleID !== undefined && a.scheduleID > -1) {
          a.schedule = this.globalWaterSchedules.find(g => g.id === a.scheduleID);
          a.scheduleName = (a.schedule !== undefined) ? a.schedule.name : '';
        } else if (a.scheduleName !== undefined) {
          a.schedule = this.globalWaterSchedules.find(g => g.name.toLowerCase() === a.scheduleName.toLowerCase());
          a.scheduleID = (a.schedule !== undefined) ? a.schedule.id : -1;

          if (a.scheduleID === -1) {
            console.log('Note: EventID: ' + event.id + ', name: ' + event.name + ' is linked to a schedule (' + a.scheduleName + ') that does not exist. Please fix');
          }
        }
      }
    });
  }


  getSensorGroups(): void {
    this.sensorGroupsLoaded = false;
    this._sensorGroupService.getSensorGroupWithSensorsAndReadingTypes().subscribe(
      results => {
        if (results != null) {
          this.sensorGroups = results;
          //this.sortSensorGroupsByName();
          //this.common.sortArrayByNameParm(this.sensorGroups);
          this.mapSensorGroups2SensorConditions();
          this.mapSensorDataTypes2SensorEvents();

          this.events.forEach(e => {
            e.sensorConditions.forEach(s => s.sensorGroup = this.sensorGroups.find(x => x.id == s.sensorGroupID))
          });

          this.sensorGroupsLoaded = true;
        } else {
          this.toastr.warning('No Sensor Groups found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving sensor groups.');
      });
  }


  sortSensorGroupsByName(): void {
    this.sensorGroups.sort((a, b) => {
      if (b.name > a.name) {
        return -1;
      }
      if (b.name < a.name) {
        return 1;
      }
    });
  }


  getNameForSensorGroupID(sensorGroupId: number): string {
    const sensorGroup = this.sensorGroups.find(x => x.id === sensorGroupId);
    return sensorGroup != null ? sensorGroup.name : '';
  }


  private getNameForSensorReadingTypeID(sensorGroupId: number, readingTypeId: number): string {
    const readingType = this.getReadingTypesForSensorGroupId(sensorGroupId).find(x => x.id == readingTypeId);
    return readingType != null ? readingType.type : '';
  }


  mapSensorGroups2SensorConditions(): void {
    this.events.forEach(e => {
      e.sensorConditions.forEach(s => s.sensorGroup = this.sensorGroups.find(x => x.id == s.sensorGroupID));
    });
  }


  mapSensorDataTypes2SensorEvents(): void {
    this.events.forEach(e => {
      e.sensorConditions.forEach(s => s.sensorReadingType.type = this.getNameForSensorReadingTypeID(s.sensorGroup.id, s.sensorReadingType.id));
    });
  }


  private getReadingTypesForSensorGroupId(sensorGroupId: number): SensorReadingType[] {
    var sensorReadingTypes: SensorReadingType[] = [];
    const sensorGroup = this.sensorGroups.find(x => x.id == sensorGroupId);

    if (sensorGroup == undefined || sensorGroup.sensors == undefined || sensorGroup.sensors.length < 1) {
      return sensorReadingTypes;
    }

    sensorGroup.sensors.forEach(x => {
      x.sensorReadingTypes.forEach(y => {
        if (sensorReadingTypes.find(z => z.id == y.id) == undefined) {
          var readingType: SensorReadingType = {
            id: y.id,
            type: y.type,
            checked: false
          }
          sensorReadingTypes.push(readingType);
        }
      });
    });

    return sensorReadingTypes;
  }


  updateSelectedDevice(selectedDevice: Device) {
    this.getGatewayVersionInfo(selectedDevice);
    this.initializeCameraPreviews();

    if (selectedDevice == undefined || selectedDevice.serialNumber == undefined || selectedDevice.serialNumber == '') {
      return;
    }

    this.selectedDevice = this.devices.find(x => x.name == selectedDevice.name);
    //this.selectedDeviceName = this.selectedDevice.name;

    this.associatedCameraEvents = [];

    this.events.forEach(x => {
      var cameraAction = x.eventActions.filter(y => y.deviceSerial == this.selectedDevice.serialNumber && (y.type == ActionType.Task || y.type == ActionType.Camera));
      var cameraMotionEvent = x.motionConditions.filter(y => y.deviceSerial == this.selectedDevice.serialNumber);

      if (cameraAction.length > 0 || cameraMotionEvent.length > 0) {
        this.associatedCameraEvents.push(x);
      }
    });

    this.selectedDeviceOwner = this.getOwnerName(this.selectedDevice);

    if (this.selectedDevice.deviceCameras.length > 0) {
      this.common.sortArrayByNameParm(this.selectedDevice.deviceCameras);
      this.updateSelectedCamera(this.selectedDevice.deviceCameras[0]);
    }
    else {
      this.updateSelectedCamera(undefined);
    }
  }


  updateSelectedCamera(camera: DeviceCamera) {
    this.selectedCamera = undefined;

    if (camera == undefined || camera.type == '' || camera.model == '') {
      return;
    }

    this.selectedCamera = camera;

    if (this.selectedCamera.presets == undefined || this.selectedCamera.presets.length < 2) {
      this.selectedCamera.presets = [];

      for (var count = 1; count < 65; count++) {
        var preset: PTZCameraPreset = {
          locId: count,
          name: '',
          focus: 0,
          zoom: 0,
          lastUpdate: '',
          displayText: ''
        };

        this.selectedCamera.presets.push(preset);
      }
    }

    this.selectedCamera.presets.forEach(x => x.displayText = this.generateCameraPresetText(x) );
  }


  generateCameraPresetText(preset: PTZCameraPreset): string {
    return `Loc ${preset.locId} | Name (${preset.name}) - Focus(${preset.focus}) - Zoom (${preset.zoom})`
  }


  getOwnerName(device: Device): string {
    if(device.userGroupId > 0){
      return this.userGroups.find(x => x.id == device.userGroupId).name;
    }else{
      return device.userName;
    }
  }


  buildNewTask(deviceID: number, action: string, payload: string): Task {
    const task: Task = {
      taskID: -1,
      deviceID,
      clientID: this.common.generateUUID(),
      priority: 1,
      type: 1,
      action,
      payload,
      expireTimeSec: 87,
      taskWaitIntervalSec: 3,
      waitForResult: true
    };

    return task;
  }


  addNotes2ChangeLog() {
    this.changeLogNotes = '';
    
    const params: ChangeLogNotesParams = {
      changeLogNotes: this.changeLogNotes,
      userGroupId: 0,
      category: ''
    };

    var modalRef = this.modalService.show(ChangeLogNotesComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, class: 'modal-above-confirmdialog', initialState: params });
    modalRef.content.onChangeLogNotes.subscribe((value) => {
      this.changeLogNotes = value;
    });
  }


  ptzCameraControl(previewId: number, template: TemplateRef<any>) {
    this.ptzImagePreview = "";
    //this.updateSelectedCamera(this.cameraPreview[previewId].deviceCamera);
    this.ptzControl('refresh', 1, this.cameraPreview[previewId].deviceCamera);
    this.ptzCameraControlRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true } );
  }


  ptzControl(command: string, value: number, deviceCamera: DeviceCamera, previewId: number = -1) {
    if (deviceCamera != undefined) {
      this.updateSelectedCamera(deviceCamera);
    }
    
    if (previewId > -1) {
      this.cameraPreview[previewId].scoutview.latestThumbnail.blobName = '';
      this.cameraPreview[previewId].scoutview.image.blobName = '';
      this.cameraPreview[previewId].scoutview.latestThumbnail.isLoading = true;
    }

    var task: Task;
    var loadMessage = ''

    var sourceId = this.selectedDevice.serialNumber;

    this.waitingForPTZResponse = true;
    this.ptzImageblobName = sourceId + "_" + '20200101T010101_preview.jpg';

    if (command.toLowerCase() != 'saveimage') {
      this.deleteCameraImage(sourceId, this.ptzImageblobName);
    }

    var user: string = '';
    var pass: string = '';
    var ip: string = '';

    if (this.selectedCamera.login != undefined && this.selectedCamera.login != '' && this.selectedCamera.login.includes(':')) {
      user = this.selectedCamera.login.split(':')[0];
      pass = this.selectedCamera.login.split(':')[1];
      ip = this.selectedCamera.port;
      user = user.replace(/ /g, '');
      pass = pass.replace(/ /g, '');
      ip = ip.replace(/ /g, '');
    }

    switch (command) {
      case "poweron":
        task = this.buildNewTask(this.selectedDevice.id, `/home/pi/kigs/scripts/tasks/ptzStartRemote user=${user} pass=${pass} cam_type=${this.selectedCamera.power} ipaddress=${ip} timer=15 gport=18`, '');
        loadMessage = "Powering up solar camera for 15 minutes";
        break;

      case "zoom":
        task = this.buildNewTask(this.selectedDevice.id, `/home/pi/kigs/scripts/tasks/ptzControl user=${user} pass=${pass} ipaddress=${ip} zoom=${this.ptzCameraZoom} autofocus=${this.ptzCameraAutoFocus}`, '');
        break;
    
      case "focus":
        task = this.buildNewTask(this.selectedDevice.id, `/home/pi/kigs/scripts/tasks/ptzControl user=${user} pass=${pass} ipaddress=${ip} focus=${this.ptzCameraFocus} autofocus=false`, '');
        break;

      case "setpreset":
        if (this.selectedCameraPreset == undefined || this.selectedCameraPreset.locId < 1) {
          this.toastr.warning('Please select a location in the dropdown box');
          return;
        }       
        task = this.buildNewTask(this.selectedDevice.id, `/home/pi/kigs/scripts/tasks/ptzControl user=${user} pass=${pass} ipaddress=${ip} ${command.toLowerCase()}=${value}`, '');
        break;

      case "gotopreset":
        if (this.selectedCameraPreset == undefined || this.selectedCameraPreset.locId < 1) {
          this.toastr.warning('Please select a location in the dropdown box');
          return;
        }
        task = this.buildNewTask(this.selectedDevice.id, `/home/pi/kigs/scripts/tasks/ptzControl user=${user} pass=${pass} ipaddress=${ip} focus=${this.selectedCameraPreset.focus} ${command.toLowerCase()}=${value}`, '');
        break;

      case "enableremoteaccess":
        var duration = Number(this.enableRemoteAccessToCameraDuration);
        if (duration < 1) duration = 1;
        if (duration > 60) duration = 60;
        this.enableRemoteAccessToCameraDuration = duration.toString();
        task = this.buildNewTask(this.selectedDevice.id, `/home/pi/kigs/scripts/tasks/ptzStartRemote user=${user} pass=${pass} cam_type=${this.selectedCamera.power} ipaddress=${ip} timer=${this.enableRemoteAccessToCameraDuration} gport=18`, '');
        loadMessage = "Enabling Remote Access";
        break;

      case "capturevideo":
        task = this.buildNewTask(this.selectedDevice.id, `/home/pi/kigs/scripts/tasks/ptzControl user=${user} pass=${pass} ipaddress=${ip} autofocus=true mode=video duration=${this.captureVideoNowDuration}`, '');
        break;
        
      default:
        var cmdValue = value * this.ptzCameraStep * 5.0;
        task = this.buildNewTask(this.selectedDevice.id, `/home/pi/kigs/scripts/tasks/ptzControl user=${user} pass=${pass} ipaddress=${ip} ${command.toLowerCase()}=${cmdValue} autofocus=${this.ptzCameraAutoFocus} cam_type=${this.selectedCamera.power} gport=18`, '');
        break;
    }

    this.deviceService.postTask(task, loadMessage, 4).subscribe(
      results => {
        if (results != null) {   
          var mediaCaptureResults = this.parseMediaCaptureResults(results);
          this.ptzCameraFocus = mediaCaptureResults.focus;
          this.ptzCameraZoom = mediaCaptureResults.zoom;

          if (command != 'saveimage' && command != 'setpreset' && command != 'enableremoteaccess') {
            this.downloadImageAsBase64(sourceId, this.ptzImageblobName, this.waitingForPTZResponse).subscribe(x => this.ptzImagePreview = x);
          }

          if (command == 'capturevideo') {
            if (mediaCaptureResults.status == "success") {
              var image = mediaCaptureResults.filename;
              this.cameraPreview[previewId].scoutview.image.blobName = image;
              this.cameraPreview[previewId].scoutview.svMetadata.sourceId = sourceId;
  
              this.downloadImageAsBase64(sourceId, image, false).subscribe(x => {
                this.cameraPreview[previewId].scoutview.image.source = x;
                this.cameraPreview[previewId].scoutview.latestThumbnail.isLoading = false;
                this.refreshImages();
              });
            }
            else {
              this.toastr.error('Error occurred while capturing video.');
              this.cameraPreview[previewId].scoutview.latestThumbnail.isLoading = false;
            }
          }

          if (command == 'setpreset') {
            this.saveCameraPreset(this.selectedCameraPreset);
          }

          if (command == 'poweron') {
            this.toastr.success('Solar camera powered on for 10 minutes');
          }

          if (command == 'gotopreset') {
            this.ptzCameraAutoFocus = (this.selectedCameraPreset.focus == 0) ? true : false;
          }

          if (command == "enableremoteaccess") {
            this.toastr.success('Remote access enabled for ' + this.enableRemoteAccessToCameraDuration + ' minutes');
            var ipAddresses = this.selectedDevice.ipAddress.split(' ');
            var ipAddress = ipAddresses.find(x => !x.startsWith('192.168.0') && !x.startsWith('169.254'));
            this.showPTZCameraInfo(ipAddress);
          }
        }
        else {
          this.toastr.warning('Error sending command to camera');
        }
        this.waitingForPTZResponse = false;
      },
      err => {
        this.waitingForPTZResponse = false;
        this.toastr.error('Error occurred while sending command to camera');
      }
    );
  }


  showPTZCameraInfo(gatewayIP: string): void {
    var user = this.selectedCamera.login.split(':')[0];
    var pass = this.selectedCamera.login.split(':')[1];
    user = user.replace(/ /g, '');
    pass = pass.replace(/ /g, '');

    this.confirmationService.confirm({
      message: `To use the camera's native web interface, click continue and a new browser tab will open.\nEnter credentials U: ${user}, P: ${pass}`,
      header: 'PTZ Camera Information',
      icon: 'glyphicon glyphicon-ok-circle',
      key: 'openbrowser',
      accept: () => {
        const url = `https://${gatewayIP}`;
        window.open(url, '_blank');
      },
      reject: () => {
      }
    });
  }


  saveCameraPreset(cameraPreset: PTZCameraPreset) {
    var origPreset = this.selectedCamera.presets.find(x => x.locId == cameraPreset.locId);

    if (origPreset != undefined) {
      origPreset.focus = (this.ptzCameraAutoFocus) ? 0 : this.ptzCameraFocus;   // Focus=0 signals to the Scoutview device to auto-focus
      origPreset.zoom = this.ptzCameraZoom;
      origPreset.name = cameraPreset.name;
      origPreset.displayText = this.generateCameraPresetText(origPreset);
      origPreset.lastUpdate = moment.utc().format("YYYY-MM-DDTHH:mm:ss");
    
      this.selectedCameraPreset = JSON.parse(JSON.stringify(origPreset));
      this.selectedCamera.presets = [...this.selectedCamera.presets];

      var deviceCameraInfo = this.deviceMapper.mapDeviceCamera2DeviceCameraInfo([this.selectedCamera])[0];

      this.deviceService.updateDeviceCamera(deviceCameraInfo).subscribe(
        results => {
            this.toastr.success('Location Saved');
        },
        err => {
          this.toastr.error('Error occurred while updating camera position location');
        });
    }
  }


  selectedCameraPresetChanged(cameraPreset: PTZCameraPreset) {   // Make a copy to display so edit that hasn't been saved doesn't mess with the orignal
    if (cameraPreset != undefined) {
      if (cameraPreset.focus != 0 || cameraPreset.zoom != 0) {
        this.selectedCameraPreset = JSON.parse(JSON.stringify(cameraPreset));
        this.ptzCameraFocus = this.selectedCameraPreset.focus;
        this.ptzCameraZoom = this.selectedCameraPreset.zoom;
      }
    }
  }


  newCameraPresetSelection() {
    this.selectedCameraPreset = {
      locId: 0,
      name: "",
      focus: 0,
      zoom: 0,
      lastUpdate: '',
      displayText: "Select camera location to 'Save' or to 'Goto'"
    };
  }


  downloadImageAsBase64(sourceId: string, blobName: string, isLoading: boolean): Observable<string> {
    var image = new Subject<string>();

    this._scoutviewService.getSvImageAsBase64(sourceId, blobName, '').subscribe(
      results => {
        if (blobName.endsWith(".jpg")) {
        image.next("data:image/jpeg;base64," + results);
        }
        else if (blobName.endsWith(".mp4")) {
          image.next("data:video/mp4;base64," + results);
        }
        else if (blobName.endsWith(".webm")) {
          image.next("data:video/webm;base64," + results);
        }
        else {
          image.next("data:image/jpeg;base64,iVBORw0KGgoAAAANSUhEUgAAAQAAAAEACAIAAADTED8xAAADMElEQVR4nOzVwQnAIBQFQYXff81RUkQCOyDj1YOPnbXWPmeTRef+/3O/OyBjzh3CD95BfqICMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMO0TAAD//2Anhf4QtqobAAAAAElFTkSuQmCC");
        }

        isLoading = false;
      },
      err => {
        isLoading = false;
      });

    return image.asObservable();
  }


  showImage(scoutview: Scoutview, svImage: ScoutviewImage, forceLoad: boolean) {
    this.downloadSvImageForViewing(scoutview, svImage, forceLoad, undefined);
  }

  openFullscreenViewer(scoutview: Scoutview) {
    this.showFullscreenViewer = true;
    this.viewerZoomLevel = 1;
    this.viewerPanX = 0;
    this.viewerPanY = 0;

    if (scoutview.image.source) {
      this.viewerImageSrc = scoutview.image.source;
      this.viewerImageLoading = false;
    } else {
      this.viewerImageLoading = true;
      this.viewerImageSrc = '';
      this._scoutviewService.getSvImageAsBase64(scoutview.svMetadata.sourceId, scoutview.image.blobName, '').subscribe(
        results => {
          scoutview.image.source = 'data:image/jpeg;base64,' + results;
          this.viewerImageSrc = scoutview.image.source;
          scoutview.image.isLoading = false;
          this.viewerImageLoading = false;
        },
        err => {
          this.viewerImageLoading = false;
          this.toastr.error('Error loading full-size image');
        });
    }
  }

  closeFullscreenViewer() {
    this.showFullscreenViewer = false;
    this.viewerImageSrc = '';
  }

  viewerZoomIn() {
    this.viewerZoomLevel = Math.min(10, this.viewerZoomLevel + 0.25);
  }

  viewerZoomOut() {
    this.viewerZoomLevel = Math.max(0.25, this.viewerZoomLevel - 0.25);
    if (this.viewerZoomLevel <= 1) {
      this.viewerPanX = 0;
      this.viewerPanY = 0;
    }
  }

  viewerResetView() {
    this.viewerZoomLevel = 1;
    this.viewerPanX = 0;
    this.viewerPanY = 0;
    this.viewerBoxZoomMode = false;
    this.boxZoomActive = false;
  }

  toggleBoxZoomMode() {
    this.viewerBoxZoomMode = !this.viewerBoxZoomMode;
    this.boxZoomActive = false;
  }

  getBoxRect(): { left: number; top: number; width: number; height: number } {
    const left = Math.min(this.boxStartX, this.boxCurrentX);
    const top = Math.min(this.boxStartY, this.boxCurrentY);
    const width = Math.abs(this.boxCurrentX - this.boxStartX);
    const height = Math.abs(this.boxCurrentY - this.boxStartY);
    return { left, top, width, height };
  }

  onViewerWheel(event: WheelEvent) {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.15 : 0.15;
    this.viewerZoomLevel = Math.min(10, Math.max(0.25, this.viewerZoomLevel + delta));
    if (this.viewerZoomLevel <= 1) {
      this.viewerPanX = 0;
      this.viewerPanY = 0;
    }
  }

  onViewerMouseDown(event: MouseEvent) {
    if (this.viewerBoxZoomMode) {
      event.preventDefault();
      this.boxZoomActive = true;
      this.boxStartX = event.clientX;
      this.boxStartY = event.clientY;
      this.boxCurrentX = event.clientX;
      this.boxCurrentY = event.clientY;
      return;
    }
    if (this.viewerZoomLevel > 1) {
      event.preventDefault();
      this.viewerIsPanning = true;
      this.viewerLastX = event.clientX;
      this.viewerLastY = event.clientY;
    }
  }

  onViewerMouseMove(event: MouseEvent) {
    if (this.boxZoomActive) {
      this.boxCurrentX = event.clientX;
      this.boxCurrentY = event.clientY;
      return;
    }
    if (!this.viewerIsPanning) return;
    const dx = event.clientX - this.viewerLastX;
    const dy = event.clientY - this.viewerLastY;
    this.viewerPanX += dx / this.viewerZoomLevel;
    this.viewerPanY += dy / this.viewerZoomLevel;
    this.viewerLastX = event.clientX;
    this.viewerLastY = event.clientY;
  }

  onViewerMouseUp() {
    if (this.boxZoomActive) {
      this.boxZoomActive = false;
      this.applyBoxZoom();
      return;
    }
    this.viewerIsPanning = false;
  }

  private applyBoxZoom() {
    const rect = this.getBoxRect();
    if (rect.width < 20 || rect.height < 20) return;

    const imgEl = document.querySelector('.fullscreen-viewer-image') as HTMLElement;
    const containerEl = document.querySelector('.fullscreen-viewer-container') as HTMLElement;
    if (!imgEl || !containerEl) return;

    const imgRect = imgEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();

    const imgCenterX = imgRect.left + imgRect.width / 2;
    const imgCenterY = imgRect.top + imgRect.height / 2;

    const boxCenterScreenX = rect.left + rect.width / 2;
    const boxCenterScreenY = rect.top + rect.height / 2;

    const offsetX = (boxCenterScreenX - imgCenterX) / this.viewerZoomLevel;
    const offsetY = (boxCenterScreenY - imgCenterY) / this.viewerZoomLevel;

    const boxWidthImg = rect.width / this.viewerZoomLevel;
    const boxHeightImg = rect.height / this.viewerZoomLevel;

    const newZoom = Math.min(
      10,
      Math.max(0.25,
        Math.min(containerRect.width / boxWidthImg, containerRect.height / boxHeightImg)
      )
    );

    this.viewerPanX = this.viewerPanX - offsetX;
    this.viewerPanY = this.viewerPanY - offsetY;
    this.viewerZoomLevel = newZoom;

    this.viewerBoxZoomMode = false;
  }


  downloadSvImageForViewing(scoutview: Scoutview, svImage: ScoutviewImage, forceLoad: boolean, mediaGallery: MediaGallery) {
    if (svImage.source != '' && !forceLoad) {     // Image already loaded into memory. Don't load again unless forced to
      return;   
    }

    svImage.isLoading = true;

    this._scoutviewService.getSvImageAsBase64(scoutview.svMetadata.sourceId, svImage.blobName, '').subscribe(
      results => {
        if (svImage.blobName.endsWith(".jpg")) {
          svImage.source = "data:image/jpeg;base64," + results;
        }
        else if (svImage.blobName.endsWith(".mp4")) {
          svImage.source = "data:video/mp4;base64," + results;
        }
        else if (svImage.blobName.endsWith(".webm")) {
          svImage.source = "data:video/webm;base64," + results;
        }

        if (mediaGallery != undefined) {
          mediaGallery.itemImageSrc = svImage.source;
        }

        svImage.isLoading = false;
        svImage.title = '';
      },
      err => {
        svImage.isLoading = false;
        svImage.title = '';
      });
  }


  deleteCameraImage(sourceId: string, filename: string) {
    this._scoutviewService.deleteSvImagesAndMetadata(sourceId, filename).subscribe(
      results => {
      },
      err => {
      }
    );
  }


  getGatewayVersionInfo(device: Device) {
    device.version = 'checking';

    const task = this.buildNewTask(device.id, '/bin/cat /home/pi/kigs/version.txt', '');
    this.deviceService.postTask(task, '', 4).subscribe(
      results => {
        if (results != undefined && results != null && results != '') {
          device.version = results;
          device.versionWarning = !this.isDeviceVersionEqualOrGreaterTo(device, '5.6.0');
        } else {
          device.version = 'Load';
          device.versionWarning = true;
        }
      },
      err => {
        device.versionWarning = true;
      });
  }


  isDeviceVersionEqualOrGreaterTo(device: Device, version: string): boolean {
    var deviceVersion = device.version.split('.').map(Number);
    while (deviceVersion.length < 3) { deviceVersion.push(0) }

    var compareVersion = version.split('.').map(Number);
    while (compareVersion.length < 3) { compareVersion.push(0) } 

    var warningMsg = "Scoutview gateway needs to be updated to v5.6.0 or greater. Contact support.";
    device.versionInfo = "";

    if (deviceVersion[0] > compareVersion[0]) {
      return true;
    }

    if (deviceVersion[0] < compareVersion[0]) {
      device.versionInfo = warningMsg;
      return false;
    }

    if (deviceVersion[1] > compareVersion[1]) {
      return true;
    }
    if (deviceVersion[1] < compareVersion[1]) {
      device.versionInfo = warningMsg;
      return false;
    }

    if (deviceVersion[2] > compareVersion[2]) {
      return true;
    }
    if (deviceVersion[2] < compareVersion[2]) {
      device.versionInfo = warningMsg;
      return false;
    }

    return true;
  }

}
